/**
 * Network and server error detection and categorisation utilities
 */

// ============================================================================
// Shared Error Detection Constants and Functions
// ============================================================================

/**
 * Mapping from tRPC error codes to HTTP status codes.
 * Used as a fallback when httpStatus is not directly available on the error object.
 */
export const TRPC_CODE_TO_HTTP_STATUS: Record<string, number> = {
    // 5xx Server Errors
    INTERNAL_SERVER_ERROR: 500,
    BAD_GATEWAY: 502,
    SERVICE_UNAVAILABLE: 503,
    TIMEOUT: 504,
    GATEWAY_TIMEOUT: 504,
    // 4xx Client Errors
    BAD_REQUEST: 400,
    UNAUTHORIZED: 401,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    METHOD_NOT_SUPPORTED: 405,
    CONFLICT: 409,
    PRECONDITION_FAILED: 412,
    PAYLOAD_TOO_LARGE: 413,
    UNPROCESSABLE_CONTENT: 422,
    TOO_MANY_REQUESTS: 429,
    CLIENT_CLOSED_REQUEST: 499,
};

/**
 * tRPC error codes that indicate server errors (5xx)
 */
export const SERVER_ERROR_TRPC_CODES = [
    'INTERNAL_SERVER_ERROR',
    'BAD_GATEWAY',
    'SERVICE_UNAVAILABLE',
    'TIMEOUT',
    'GATEWAY_TIMEOUT',
] as const;

/**
 * Message patterns that indicate server errors when other detection methods fail
 */
export const SERVER_ERROR_MESSAGE_PATTERNS = ['unknown error', 'internal server error'];

/**
 * Check if an error appears to be a server error based on its message
 */
export function isServerErrorByMessage(error: unknown): boolean {
    if (!error) return false;
    const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
    const lowerMessage = message.toLowerCase();
    return SERVER_ERROR_MESSAGE_PATTERNS.some((pattern) => lowerMessage.includes(pattern));
}

/**
 * Extract HTTP status code from an error object.
 * Checks multiple locations in order of priority.
 */
export function getHttpStatus(error: unknown): number | null {
    if (!error || typeof error !== 'object') return null;

    // 1. Check error.data.httpStatus (standard location after server fix)
    if ('data' in error && error.data && typeof error.data === 'object') {
        if ('httpStatus' in error.data && typeof error.data.httpStatus === 'number') {
            return error.data.httpStatus;
        }

        // 2. Check error.data.code and map to HTTP status
        if ('code' in error.data && typeof error.data.code === 'string') {
            const trpcCode = error.data.code;
            if (trpcCode in TRPC_CODE_TO_HTTP_STATUS) {
                return TRPC_CODE_TO_HTTP_STATUS[trpcCode];
            }
        }
    }

    // 3. Fallback: Check top-level error.code (for TRPCClientError)
    if ('code' in error && typeof error.code === 'string') {
        const topLevelCode = error.code;
        if (topLevelCode in TRPC_CODE_TO_HTTP_STATUS) {
            return TRPC_CODE_TO_HTTP_STATUS[topLevelCode];
        }
    }

    // 4. tRPC transport status: httpBatchStreamLink/httpLink attach the failed fetch
    // Response under `error.meta.response` (e.g. a Cloudflare 524 / proxy 502-504 HTML
    // page that fails JSON parsing). This is the only place the real status survives
    // when the body is not a tRPC error shape, so check it before the generic props.
    // Narrowed structurally (no `any`) because the shape is untyped at this boundary.
    if ('meta' in error) {
        const meta = (error as { meta?: unknown }).meta;
        if (meta && typeof meta === 'object') {
            const response = (meta as { response?: unknown }).response;
            if (response && typeof response === 'object') {
                const status = (response as { status?: unknown }).status;
                if (typeof status === 'number') {
                    return status;
                }
            }
        }
    }

    // 5. Direct status properties
    if ('status' in error && typeof error.status === 'number') {
        return error.status;
    }
    if ('statusCode' in error && typeof (error as any).statusCode === 'number') {
        return (error as any).statusCode;
    }
    if ('response' in error && (error as any).response?.status) {
        return (error as any).response.status;
    }

    // 6. Try to parse from message (e.g., "status code 500")
    const message = getErrorMessage(error);
    const statusMatch = message.match(/status\s+(?:code\s+)?(\d+)/i);
    if (statusMatch) {
        return parseInt(statusMatch[1], 10);
    }

    return null;
}

/**
 * Check if an error is a server error (5xx HTTP status)
 */
export function isServerError(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;

    // First check HTTP status
    const httpStatus = getHttpStatus(error);
    if (typeof httpStatus === 'number' && httpStatus >= 500 && httpStatus < 600) {
        return true;
    }

    // Fallback: check tRPC error code directly (in data or top-level)
    const trpcCode = (error as any).data?.code || (error as any).code;
    if (typeof trpcCode === 'string') {
        if (SERVER_ERROR_TRPC_CODES.includes(trpcCode as (typeof SERVER_ERROR_TRPC_CODES)[number])) {
            return true;
        }
    }

    // Final fallback: message-based detection for "Unknown error" from tRPC
    if (isServerErrorByMessage(error)) {
        return true;
    }

    return false;
}

// ============================================================================
// Network Error Types and Interfaces
// ============================================================================

export type NetworkErrorType =
    | 'offline'
    | 'timeout'
    | 'dns'
    | 'server_error'
    | 'cors'
    | 'network_failed'
    | 'connection_refused'
    | 'ssl'
    | 'unknown';

export type ConnectionQuality = 'good' | 'poor' | 'offline';

export interface NetworkErrorDetails {
    type: NetworkErrorType;
    title: string;
    userMessage: string;
    technicalMessage: string;
    suggestions: string[];
    isRetryable: boolean;
    retryDelay?: number; // milliseconds
    connectionQuality?: ConnectionQuality;
    consecutiveFailures?: number;
    lastSuccessfulConnection?: Date;
}

/**
 * Detects if an error is a network-related error
 */
export function isNetworkError(error: unknown): boolean {
    if (!error) return false;

    const message = getErrorMessage(error).toLowerCase();
    const hasErrorData = hasStructuredErrorData(error);

    // If there's no error data and message exists, it's likely a network error
    if (!hasErrorData && message.length > 0) {
        const indicators = [
            'network',
            'fetch',
            'offline',
            'failed to fetch',
            'load failed',
            'timeout',
            'timed out',
            'net::err',
            'ecconnreset',
            'econnrefused',
            'connection reset',
            'connection refused',
            'status code 0',
            'status 0',
            'dns',
            'cors',
            'ssl',
            'certificate',
        ];

        return indicators.some((indicator) => message.includes(indicator));
    }

    // Check for HTTP status codes that indicate server/network issues
    if (hasStructuredErrorData(error)) {
        const statusCode = getHttpStatus(error);
        if (statusCode !== null) {
            // 5xx errors are server errors, but still network-related from user's perspective
            // 0 status means network failure
            return statusCode === 0 || (statusCode >= 500 && statusCode < 600);
        }
    }

    return false;
}

/**
 * Categorises a network error and provides detailed information
 * @param error - The error to categorise
 * @param connectionQuality - Optional connection quality from AppStateStore
 * @param consecutiveFailures - Optional consecutive failures count
 */
export function categoriseNetworkError(
    error: unknown,
    connectionQuality?: ConnectionQuality,
    consecutiveFailures?: number,
): NetworkErrorDetails {
    const message = getErrorMessage(error).toLowerCase();
    const statusCode = getHttpStatus(error);

    // Offline detection
    if (!navigator.onLine || message.includes('offline')) {
        return {
            type: 'offline',
            title: 'No Internet Connection',
            userMessage: 'Your device appears to be offline. Please check your internet connection and try again.',
            technicalMessage: getErrorMessage(error),
            suggestions: [
                'Check your Wi-Fi or mobile data connection',
                'Try disabling and re-enabling your network connection',
                'Contact your network administrator if the problem persists',
            ],
            isRetryable: true,
            retryDelay: 2000,
            connectionQuality: connectionQuality || 'offline',
            consecutiveFailures,
        };
    }

    // Timeout errors
    if (message.includes('timeout') || message.includes('timed out')) {
        return {
            type: 'timeout',
            title: 'Request Timeout',
            userMessage:
                'The server took too long to respond. This might be due to a slow connection or high server load.',
            technicalMessage: getErrorMessage(error),
            suggestions: [
                'Check your internet connection speed',
                'Try again in a few moments',
                'If the problem persists, the server may be experiencing high load',
            ],
            isRetryable: true,
            retryDelay: 3000,
            connectionQuality,
            consecutiveFailures,
        };
    }

    // DNS errors
    if (message.includes('dns') || message.includes('getaddrinfo') || message.includes('name not resolved')) {
        return {
            type: 'dns',
            title: 'Server Not Found',
            userMessage: 'Unable to locate the server. This might be a temporary network issue.',
            technicalMessage: getErrorMessage(error),
            suggestions: [
                'Check your internet connection',
                'Try refreshing the page',
                'Clear your browser cache and DNS cache',
                'Contact support if the problem persists',
            ],
            isRetryable: true,
            retryDelay: 5000,
            connectionQuality,
            consecutiveFailures,
        };
    }

    // CORS errors
    if (message.includes('cors') || message.includes('cross-origin')) {
        return {
            type: 'cors',
            title: 'Connection Blocked',
            userMessage:
                'The connection was blocked by security settings. This is typically a server configuration issue.',
            technicalMessage: getErrorMessage(error),
            suggestions: [
                'Try refreshing the page',
                'Clear your browser cache',
                'Contact support with the error details',
            ],
            isRetryable: false,
            connectionQuality,
            consecutiveFailures,
        };
    }

    // SSL/Certificate errors
    if (message.includes('ssl') || message.includes('certificate') || message.includes('https')) {
        return {
            type: 'ssl',
            title: 'Secure Connection Failed',
            userMessage: 'Unable to establish a secure connection. This might be a security certificate issue.',
            technicalMessage: getErrorMessage(error),
            suggestions: [
                'Check your system date and time settings',
                'Try refreshing the page',
                'Contact support if the problem persists',
            ],
            isRetryable: true,
            retryDelay: 3000,
            connectionQuality,
            consecutiveFailures,
        };
    }

    // Connection refused
    if (message.includes('connection refused') || message.includes('econnrefused')) {
        return {
            type: 'connection_refused',
            title: 'Connection Refused',
            userMessage:
                'The server refused the connection. This might be due to server maintenance or firewall settings.',
            technicalMessage: getErrorMessage(error),
            suggestions: [
                'Wait a few moments and try again',
                'Check if other websites are accessible',
                'Contact support if the problem persists',
            ],
            isRetryable: true,
            retryDelay: 5000,
            connectionQuality,
            consecutiveFailures,
        };
    }

    // Server errors (5xx)
    if (statusCode && statusCode >= 500 && statusCode < 600) {
        return {
            type: 'server_error',
            title: 'Server Error',
            userMessage: `The server encountered an error (${statusCode}). This is not your fault - our team has been notified.`,
            technicalMessage: getErrorMessage(error),
            suggestions: [
                'Wait a few moments and try again',
                'The issue should be resolved shortly',
                'Contact support if the problem persists',
            ],
            isRetryable: true,
            retryDelay: 5000,
            connectionQuality,
            consecutiveFailures,
        };
    }

    // Generic network failure
    if (message.includes('failed to fetch') || message.includes('network') || statusCode === 0) {
        return {
            type: 'network_failed',
            title: 'Connection Failed',
            userMessage: 'Unable to reach the server. Please check your internet connection.',
            technicalMessage: getErrorMessage(error),
            suggestions: [
                'Check your internet connection',
                'Try refreshing the page',
                'If using a VPN, try disconnecting and reconnecting',
                'Contact support if the problem persists',
            ],
            isRetryable: true,
            retryDelay: 3000,
            connectionQuality,
            consecutiveFailures,
        };
    }

    // Unknown network error
    return {
        type: 'unknown',
        title: 'Connection Error',
        userMessage: 'An unexpected connection error occurred. Please try again.',
        technicalMessage: getErrorMessage(error),
        suggestions: ['Try refreshing the page', 'Check your internet connection', 'Contact support if needed'],
        isRetryable: true,
        retryDelay: 3000,
        connectionQuality,
        consecutiveFailures,
    };
}

/**
 * Helper to get error message from various error types
 */
function getErrorMessage(error: unknown): string {
    if (typeof error === 'string') return error;
    if (error instanceof Error) return error.message;
    if (error && typeof error === 'object' && 'message' in error) {
        return String(error.message);
    }
    return 'Unknown error';
}

/**
 * Check if error has structured data (indicates it's not a pure network error)
 */
function hasStructuredErrorData(error: unknown): boolean {
    if (!error || typeof error !== 'object') return false;
    if ('data' in error && error.data && typeof error.data === 'object') {
        // Check if data has actual content (not just empty object)
        return Object.keys(error.data).length > 0;
    }
    return false;
}

/**
 * Calculate retry delay with exponential backoff
 */
export function calculateRetryDelay(attemptNumber: number, baseDelay: number = 1000): number {
    const maxDelay = 30000; // 30 seconds max
    const exponentialDelay = baseDelay * Math.pow(2, attemptNumber - 1);
    const jitter = Math.random() * 1000; // Add some randomness
    return Math.min(exponentialDelay + jitter, maxDelay);
}
